/**
 * Asset upload — wraps POST /v1/assets multipart per ED-02 §K.
 *
 * The editor-react `<EmailBuilder>` consumes this through its host
 * adapter prop (`hostAdapter.uploadAsset`). The function returns just
 * the public URL because that's all the editor needs to populate the
 * image block's `src.url` field; richer metadata (width / size_bytes /
 * etc.) is fetched via GET /v1/assets when the user opens the asset
 * library (Phase 3).
 */
import { ApiError } from "./api";
import { mintTraceparent } from "./trace";
import { getAuthToken } from "../stores/auth";

const SESSION_TRACEPARENT = mintTraceparent();

export interface AssetUploadResult {
  id: string;
  url: string;
  format: string;
  mime_type: string;
  width: number | null;
  height: number | null;
  size_bytes: number;
  original_name: string;
  created_at: string;
}

const baseUrl = import.meta.env["VITE_API_BASE_URL"] ?? "";

export async function uploadAsset(file: File): Promise<AssetUploadResult> {
  const form = new FormData();
  form.append("file", file);

  const headers = new Headers();
  headers.set("Accept", "application/json");
  headers.set("traceparent", SESSION_TRACEPARENT);
  // FormData sets its own multipart boundary — do NOT set Content-Type.

  const token = getAuthToken();
  if (token !== null && token !== "") {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(baseUrl + "/v1/assets", {
    method: "POST",
    headers,
    body: form,
  });

  const contentType = response.headers.get("content-type") ?? "";

  if (!response.ok) {
    if (contentType.includes("application/problem+json")) {
      throw new ApiError(await response.json());
    }
    throw new ApiError({
      type: "about:blank",
      title: response.statusText || "Upload failed",
      status: response.status,
      detail: `HTTP ${response.status}`,
      code: "HTTP_ERROR",
    });
  }

  const body = (await response.json()) as { data: AssetUploadResult };
  return body.data;
}

/**
 * Convenience matching the editor's HostAdapter contract — returns just
 * the URL string (the editor populates the block from there).
 */
export async function uploadAssetUrl(file: File): Promise<string> {
  const result = await uploadAsset(file);
  return result.url;
}
